/**
 * Verhalten eines Formulars aus der Vorlage – reine Funktionen auf einer Kopie der Daten (der Editor ruft sie in
 * `aendern(d => …)` auf):
 *   beiAn / beiAus   Häkchen an → leere Felder vorbelegen (z. B. Datum heute, Zuständigkeit), aus → Felder setzen
 *   beiWahl          Auswahl getroffen → andere Felder setzen (Bewässerungssystem gewählt → «System» erledigt)
 *   aktion           eine Zeile («✓ Alles») oder alle Zeilen («Alle ✓») erledigen bzw. wieder öffnen
 * Werte: «heute», «ich» (Zuständigkeit wie zuletzt in diesem Formular, sonst eigener Name), «vorige» (Wert der Zeile
 * darüber bzw. zuletzt verwendet).
 */

export const istHaken = (f) => f?.typ === 'haken';
const spalte = (a, id) => a.spalten.find((f) => f.id === id);
const nurMitErlaubt = (a, f, zeileDef) => !f?.nurMit || (zeileDef?.mit ?? []).includes(f.id);

/** Alle Zeilen eines Formulars (für «ich» und Fortschritt). */
function alleZeilen(v, d) {
  return v.abschnitte.flatMap((a) => (a.art === 'tabelle' ? d[a.id] ?? [] : Object.values(d[a.id] ?? {})));
}

/**
 * Kontext für Vorbelegungen.
 * @param {{ heute: string, meinName: string, team: string[], gemerkt?: (f) => string[] }} umgebung
 */
export function kontext(v, d, umgebung) {
  const ich = () => {
    // wie zuletzt in diesem Formular eingetragen (neuestes Datum), sonst Teamname des Benutzers, sonst sein Name
    const mitDatum = alleZeilen(v, d).filter((z) => z.zustaendig && z.datum).sort((x, y) => y.datum.localeCompare(x.datum));
    if (mitDatum[0]) return mitDatum[0].zustaendig;
    const name = String(umgebung.meinName ?? '').toLowerCase();
    return umgebung.team.find((t) => name.startsWith(t.toLowerCase())) ?? umgebung.meinName ?? '';
  };
  return { ...umgebung, ich };
}

/** Wert einer Vorbelegung auflösen. */
export function aufloesen(wert, { ctx, zeilen, i, f }) {
  if (wert === 'heute') return ctx.heute;
  if (wert === 'ich') return ctx.ich();
  if (wert === 'vorige') {
    const vorher = (zeilen ?? []).slice(0, i).reverse().find((z) => z[f.id])?.[f.id];
    return vorher || ctx.gemerkt?.(f)?.[0] || '';
  }
  return wert;
}

/** Folgen eines geänderten Felds in derselben Zeile (beiAn, beiAus, beiWahl). */
function folgen(a, z, f, neu, kx) {
  if (istHaken(f)) {
    if (neu && f.beiAn) for (const [k, w] of Object.entries(f.beiAn)) { if (!z[k]) z[k] = aufloesen(w, { ...kx, f: spalte(a, k) }); }
    if (!neu && f.beiAus) for (const [k, w] of Object.entries(f.beiAus)) z[k] = w;
  } else if (f.beiWahl && neu) {
    for (const [k, w] of Object.entries(f.beiWahl)) z[k] = w;
  }
}

/** Ein Feld einer Zeile setzen (Tabelle: Index, feste Zeilen: id). Gibt die Daten zurück. */
export function setzen(v, d, a, zeile, feldId, wert, ctx) {
  const f = spalte(a, feldId);
  const zeilen = a.art === 'tabelle' ? d[a.id] : null;
  const z = a.art === 'tabelle' ? d[a.id][zeile] : d[a.id][zeile];
  z[feldId] = wert;
  folgen(a, z, f, wert, { ctx, a, zeilen, i: zeile });
  return d;
}

/** Sind alle «fertig»-Felder der Zeile erledigt? */
export const zeileFertig = (a, z) => (a.fertig ?? []).length > 0 && a.fertig.every((k) => z[k] === true);

/** Aktion einer Tabellenzeile («✓ Alles»): erledigen bzw. – wenn schon alles erledigt – wieder öffnen. */
export function zeilenAktion(v, d, a, i, ctx) {
  const zeilen = d[a.id];
  const z = zeilen[i];
  const fertig = zeileFertig(a, z);
  const setzt = Object.entries(a.aktion?.setzt ?? {});
  for (const [k, w] of setzt) {
    const f = spalte(a, k);
    if (istHaken(f)) {
      z[k] = !fertig;
      folgen(a, z, f, z[k], { ctx, a, zeilen, i }); // auch wenn schon an: leere Felder (Datum, Zuständigkeit) füllen
    } else if (!fertig && !z[k]) {
      z[k] = aufloesen(w, { ctx, a, zeilen, i, f });
    }
  }
  return d;
}

/** Aktion für alle festen Zeilen («Alle ✓»): ausser Zeilen mit `ausserWenn` (z. B. mit Notiz «Nicht vorhanden»). */
export function alleAktion(v, d, a, ctx) {
  const haken = Object.keys(a.aktion?.setzt ?? {}).map((k) => spalte(a, k)).filter(istHaken);
  const betroffen = a.zeilen.filter((zd) => !(a.aktion?.ausserWenn && d[a.id][zd.id][a.aktion.ausserWenn]));
  const passend = (zd) => haken.filter((f) => nurMitErlaubt(a, f, zd));
  const alle = betroffen.every((zd) => passend(zd).every((f) => d[a.id][zd.id][f.id] === true));
  for (const zd of betroffen) {
    const z = d[a.id][zd.id];
    for (const f of passend(zd)) {
      z[f.id] = !alle;
      folgen(a, z, f, z[f.id], { ctx, a, zeilen: null, i: 0 });
    }
  }
  return d;
}

/** Zeilen einer Tabelle auf n bringen (nur hinzufügen). */
export function zeilenAnlegen(a, d, n, leereZeile) {
  const ziel = Math.min(Math.max(n, 1), a.max);
  while (d[a.id].length < ziel) d[a.id].push({ nr: d[a.id].length + 1, ...leereZeile() });
  return d;
}

/** Zeile kopieren (direkt darunter), ohne die Felder in `kopieren.ohne` (z. B. Stromkreis, Messwerte). */
export function zeileKopieren(a, d, i) {
  if (d[a.id].length >= a.max) return d;
  const ohne = new Set(a.kopieren?.ohne ?? []);
  const kopie = Object.fromEntries(Object.entries(d[a.id][i]).map(([k, w]) => {
    const f = spalte(a, k);
    return [k, ohne.has(k) ? (f?.typ === 'haken' ? false : f?.typ === 'mehrfach' ? [] : '') : Array.isArray(w) ? [...w] : w];
  }));
  d[a.id].splice(i + 1, 0, kopie);
  d[a.id].forEach((z, n) => { z.nr = n + 1; });
  return d;
}

/** Eine Zeile entfernen (die übrigen rücken nach). */
export function zeileEntfernen(a, d, i) {
  d[a.id].splice(i, 1);
  d[a.id].forEach((z, n) => { z.nr = n + 1; });
  return d;
}

const leer = (w) => w === '' || w === null || w === undefined || (Array.isArray(w) && !w.length);
/** Pflichtfelder ohne Wert, z. B. «Ort», «Stromkreis 2: Ort» – nur als Hinweis, Speichern geht trotzdem. */
export function fehlendePflicht(v, d) {
  const out = [];
  for (const f of [...(v.kopf ?? []), ...(v.fuss ?? [])]) if (f.pflicht && leer(d[f.id])) out.push(f.titel);
  for (const a of v.abschnitte.filter((x) => x.art === 'tabelle')) {
    const pflicht = a.spalten.filter((f) => f.pflicht);
    if (a.pflichtZeilen && !d[a.id]?.length) out.push(`mindestens ein${a.zeile ? `e ${a.zeile}` : 'e Zeile'}`);
    for (const z of d[a.id] ?? []) for (const f of pflicht) if (leer(z[f.id])) out.push(`${a.zeile} ${z.nr}: ${f.titel}`);
  }
  return out;
}

/**
 * Aus einem früheren Formular übernehmen (`vorlage.uebernehmen`): gewählte Kopf-Felder und Tabellenzeilen ohne die
 * Felder in `ohne` (z. B. Messwerte). Prüfergebnisse (feste Zeilen) werden nie übernommen – sie gelten nur für die
 * damalige Prüfung.
 */
export function uebernehmen(v, d, quelle) {
  const u = v.uebernehmen ?? {};
  for (const k of u.kopf ?? []) {
    const w = quelle?.[k];
    if (w !== undefined && w !== '' && !(Array.isArray(w) && !w.length)) d[k] = Array.isArray(w) ? [...w] : w;
  }
  for (const [aid, t] of Object.entries(u.tabellen ?? {})) {
    const a = v.abschnitte.find((x) => x.id === aid);
    const ohne = new Set(t.ohne ?? []);
    const zeilen = Array.isArray(quelle?.[aid]) ? quelle[aid] : [];
    if (!a || !zeilen.length) continue;
    d[aid] = zeilen.slice(0, a.max).map((z, i) => ({
      nr: i + 1,
      ...Object.fromEntries(a.spalten.map((f) => [f.id, ohne.has(f.id) || z[f.id] === undefined
        ? (f.typ === 'haken' ? false : f.typ === 'mehrfach' ? [] : '')
        : Array.isArray(z[f.id]) ? [...z[f.id]] : z[f.id]])),
    }));
  }
  return d;
}

/** Kurz für Übersichtszeilen: «30.10.» im laufenden Jahr, sonst «30.10.25». */
export function datumKurz(iso) {
  if (!iso) return '';
  const [j, m, t] = iso.split('-');
  return j === String(new Date().getFullYear()) ? `${t}.${m}.` : `${t}.${m}.${j.slice(2)}`;
}

/** Graue Kurzinfo einer Zeile aus `kurzinfo` (Häkchen erscheinen mit ihrem Titel). */
export function kurzinfo(a, z) {
  return (a.kurzinfo ?? []).map((k) => {
    const f = spalte(a, k);
    if (istHaken(f)) return z[k] ? f.titel : '';
    if (f?.typ === 'datum') return datumKurz(z[k]);
    // Messwert mit Name und Einheit aus dem Titel: «Riso (MΩ)» → «Riso 250 MΩ»
    if (f?.typ === 'zahl' && z[k] !== '' && z[k] != null) {
      const m = f.titel.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
      return m ? `${m[1]} ${z[k]} ${m[2]}` : `${f.titel} ${z[k]}`;
    }
    if (Array.isArray(z[k])) return z[k].join(', ');
    return z[k];
  }).filter(Boolean).join(' · ');
}

/** Fortschritt eines Abschnitts: { n, gesamt }. */
export function fortschritt(a, d) {
  const zeilen = a.art === 'tabelle' ? d[a.id] ?? [] : Object.values(d[a.id] ?? {});
  const feld = a.fortschritt?.feld;
  return { n: feld ? zeilen.filter((z) => z[feld] === true).length : 0, gesamt: zeilen.length };
}

export const zeigtFeld = (a, f, zeileDef, z) => !f.nurMit || nurMitErlaubt(a, f, zeileDef) || z[f.id] === true;
